// src/components/HowToPlayPage.jsx
import React, { useEffect, useCallback, useContext } from 'react';
import PageWrapper from './PageWrapper'; // Assuming PageWrapper is in components
import { HelpCircleIcon, GamepadIcon, CreditCardIcon } from '../common/Icons'; // Icons for the page
import { LanguageContext } from '../contexts/LanguageContext'; // Import LanguageContext
import { ToastContainer, toast } from 'react-toastify'; // Import ToastContainer and toast

/**
 * HowToPlayPage component provides instructions on how to play Bingo.
 * It uses a multi-step format with descriptive text and icons.
 * It also supports internationalization for different languages.
 */
const HowToPlayPage = (props) => {
  const { language } = useContext(LanguageContext); // Use LanguageContext to get current language

  // Translations object, providing messages in multiple languages
  const translations = {
    en: {
      howToPlay: "How To Play Bingo",
      welcomeMessage: "Welcome to Bingo! Follow these simple steps to start playing and win big!",
      step1Title: "Step 1: Choose Your Stake",
      step1Desc1: "Navigate to the \"Bingo Game\" section from the dashboard or sidebar.",
      step1Desc2: "You'll see a list of available games with different stake amounts (e.g., 10 Birr, 20 Birr, etc.) and their potential winnings.",
      step1Desc3: "Select a game based on the stake you wish to play with and your potential reward.",
      step2Title: "Step 2: Select Your Cartella",
      step2Desc1: "After choosing a stake, you'll be directed to the cartella selection page.",
      step2Desc2: "Here, you can browse through various unique Bingo cartellas (boards) available for that game instance.",
      step2Desc3: "Each cartella has a unique ID and a set of numbers. Remember, the 'N' column's middle spot is always a FREE space!",
      step2Desc4: "Select the cartella you wish to play with. This cartella will be reserved for you.",
      step3Title: "Step 3: Confirm and Play!",
      step3Desc1: "Once you've selected your cartella, you'll be asked to confirm your selection and the stake amount will be deducted from your balance.",
      step3Desc2: "The game will begin shortly. Balls will be called out one by one.",
      step3Desc3: "As numbers are called, mark them on your digital cartella. The 'F' (Free) space is automatically marked.",
      step3Desc4: "Keep an eye on your board!",
      step4Title: "Step 4: Claim Your Bingo!",
      step4Desc1: "When you successfully mark five numbers in a row (horizontally, vertically, four corners or diagonally), you have a Bingo!",
      step4Desc2: "Immediately click the \"Bingo!\" button to claim your prize.",
      step4Desc3: "Our system will verify your Bingo. If valid, you win the game's potential prize, and the funds will be added to your wallet!",
      step4Desc4: "If multiple players claim Bingo simultaneously, the prize might be split, or the first valid claim wins (depending on game rules).",
      goodLuck: "Good luck and have fun playing Bingo!",
      birr: "Birr",
      fourCorners: "four corners"
    },
    am: {
      howToPlay: "ቢንጎ እንዴት እንደሚጫወት",
      welcomeMessage: "ወደ ቢንጎ እንኳን ደህና መጡ! ለመጫወት እና ትልቅ ለማሸነፍ እነዚህን ቀላል ደረጃዎች ይከተሉ!",
      step1Title: "ደረጃ 1: የውርርድዎን ይምረጡ",
      step1Desc1: "ከዳሽቦርዱ ወይም ከጎን አሞሌው ወደ \"ቢንጎ ጨዋታ\" ክፍል ይሂዱ።",
      step1Desc2: "የተለያዩ የውርርድ መጠኖች (ለምሳሌ 10 ብር, 20 ብር, ወዘተ) እና ሊያገኙዋቸው የሚችሉባቸው ሽልማቶች ያሉት የጨዋታዎች ዝርዝር ያያሉ።",
      step1Desc3: "ለመጫወት የሚፈልጉትን የውርርድ መጠን እና ሊያገኙት የሚችሉትን ሽልማት መሰረት በማድረግ ጨዋታ ይምረጡ።",
      step2Title: "ደረጃ 2: ካርቴላዎን ይምረጡ",
      step2Desc1: "ውርርድ ከመረጡ በኋላ ወደ ካርቴላ ምርጫ ገጽ ይዛወራሉ።",
      step2Desc2: "እዚህ ላይ፣ ለዚያ ጨዋታ የሚገኙትን ልዩ የቢንጎ ካርቴላዎችን (ቦርዶችን) ማየት ይችላሉ።",
      step2Desc3: "እያንዳንዱ ካርቴላ ልዩ መታወቂያ እና የቁጥሮች ስብስብ አለው። ያስታውሱ፣ የ 'N' አምድ መካከለኛ ቦታ ሁልጊዜ ነፃ ቦታ ነው!",
      step2Desc4: "ለመጫወት የሚፈልጉትን ካርቴላ ይምረጡ። ይህ ካርቴላ ለእርስዎ ተይዞ ይቆያል።",
      step3Title: "ደረጃ 3: ያረጋግጡ እና ይጫወቱ!",
      step3Desc1: "ካርቴላዎን ከመረጡ በኋላ ምርጫዎን እንዲያረጋግጡ ይጠየቃሉ እና የውርርድ መጠኑ ከሂሳብዎ ይቀነሳል።",
      step3Desc2: "ጨዋታው በቅርቡ ይጀምራል። ኳሶች አንድ በአንድ ይጣራሉ።",
      step3Desc3: "ቁጥሮች ሲጠሩ፣ በዲጂታል ካርቴላዎ ላይ ምልክት ያድርጉባቸው። 'F' (ነፃ) ቦታ በራስ-ሰር ምልክት ይደረግበታል።",
      step3Desc4: "ቦርድዎን ይከታተሉ!",
      step4Title: "ደረጃ 4: ቢንጎዎን ይጠይቁ!",
      step4Desc1: "አምስት ቁጥሮችን በተከታታይ (በአግድም፣ በአቀባዊ፣ አራቱ መዓዘናት ወይም በሰያፍ) በተሳካ ሁኔታ ምልክት ሲያደርጉ፣ ቢንጎ አግኝተዋል!",
      step4Desc2: "ሽልማትዎን ለመጠየቅ ወዲያውኑ \"ቢንጎ!\" የሚለውን ቁልፍ ይጫኑ።",
      step4Desc3: "ስርዓታችን ቢንጎዎን ያረጋግጣል። ትክክል ከሆነ፣ የጨዋታውን ሊያገኙት የሚችሉትን ሽልማት ያሸንፋሉ፣ እና ገንዘቡ ወደ የኪስ ቦርሳዎ ይታከላል!",
      step4Desc4: "በርካታ ተጫዋቾች በተመሳሳይ ጊዜ ቢንጎ ካደረጉ፣ ሽልማቱ ሊከፋፈል ይችላል፣ ወይም የመጀመሪያው ትክክለኛ የይገባኛል ጥያቄ ያሸንፋል (በጨዋታው ህጎች ላይ በመመስረት)።",
      goodLuck: "መልካም ዕድል እና ቢንጎ በመጫወት ይደሰቱ!",
      birr: "ብር",
      fourCorners: "አራቱ መዓዘናት"
    },
    or: {
      howToPlay: "Akkaataa Bingo Itti Taphachuu Qabdan",
      welcomeMessage: "Gara Bingoatti baga nagaadhaan dhuftan! Tarkaanfiiwwan salphaa kana hordofuudhaan taphachuu fi injifannoo guddaa argachuu jalqabaa!",
      step1Title: "Tarkaanfii 1: Kaffaltii Keessan Filadhaa",
      step1Desc1: "Daashboordii ykn saayidbaarii irraa gara kutaa \"Tapha Bingo\" deemuu.",
      step1Desc2: "Taphawwan adda addaa kaffaltii adda addaa (fkn, 10 Birr, 20 Birr, kkf) fi injifannoo isaan irraa argachuu dandeessan ni argitu.",
      step1Desc3: "Tapha kaffaltii taphachuu barbaaddan irratti hundaa'uun filadhaa.",
      step2Title: "Tarkaanfii 2: Kaartaa Keessan Filadhaa",
      step2Desc1: "Kaffaltii filannoo booda gara fuula filannoo kaartaa deemaa.",
      step2Desc2: "Asitti, kaartaawwan Bingo adda addaa (boordii) tapha sanaaf argaman ilaaluu dandeessu.",
      step2Desc3: "Kaartaan tokkoon tokkoon ID addaa fi lakkoofsota mataa isaa qaba. Yaadadhaa, bakki gidduu tii fiixee 'N' yeroo hundaa bakka BILISA ti!",
      step2Desc4: "Kaartaa taphachuu barbaaddan filadhaa. Kaartaan kun isiniif qabamee jira.",
      step3Title: "Tarkaanfii 3: Mirkaneessaa fi Taphadhaa!",
      step3Desc1: "Yeroo kaartaa keessan filattan, filannoo keessan akka mirkaneessitan isin gaafatama, fi kaffaltiin sun baasii keessan irraa hir'ifama.",
      step3Desc2: "Taphichi yeroo gabaabaa keessatti ni jalqaba. Baalonni tokko tokkoon ni waamamuu.",
      step3Desc3: "Lakkoofsota yeroo waamaman, kaartaa keessan dijitaalaa irratti mallattoo godhaa. Bakki 'F' (Bilisa) ofiin ofii mallattoo godhama.",
      step3Desc4: "Boordii keessan irratti ija godhaa!",
      step4Title: "Tarkaanfii 4: Bingo keessan Gaafadhaa!",
      step4Desc1: "Yeroo lakkoofsota shan tooraan (horizontal, vertical, four corners ykn diagonal) milkaa'inaan mallattoo gootan, Bingo qabduu!",
      step4Desc2: "Yeroodhuma sana \"Bingo!\" jedhu tuquudhaan badhaasa keessan gaafadhaa.",
      step4Desc3: "Sirni keenya Bingo keessan ni mirkaneessa. Yoo sirrii ta'e, badhaasa taphichaa ni injifattu, fi maallaqni gara boorsa keessaniitti ni dabalamuu!",
      step4Desc4: "Yoo taphattoonni hedduun yeroo walfakkaataa Bingo gaafatan, badhaasichi ni qoodamuu danda'a, ykn gaaffiin jalqabaa fi sirrii ta'e ni injifata (seera taphaa irratti hundaa'uun).",
      goodLuck: "Carraa gaarii fi Bingo taphachuudhaan gammadaa!",
      birr: "Birr",
      fourCorners: "mucaawwan arfan"
    },
    ti: {
      howToPlay: "ቢንጎ ብኸመይ ክትጻወት ከምዝግብኦ",
      welcomeMessage: "እንኳዕ ናብ ቢንጎ ብድሓን መጻእኩም! ንምጽዋትን ዓቢ ገንዘብ ንምዕዋትን እዞም ቀለልቲ ስጉምቲታት ተኸተሉ!",
      step1Title: "ስጉምቲ 1: ናይ ምጥራይኩም ምረጹ",
      step1Desc1: "ካብ ዳሽቦርድ ወይ ሳልሳይ ገጽ ናብቲ \"ቢንጎ ጸወታ\" ዝብል ክፋል ይኺዱ።",
      step1Desc2: "ዝተፈላለዩ መጠን ምጥራይ (ከም 10 ብር, 20 ብር, ወዘተ) ከምኡ'ውን እትረኽብዎም ሽልማታት ዝሓዙ ዝተፈላለዩ ጸወታታት ክርእዩ ኢኹም።",
      step1Desc3: "ብመሰረት እቲ ክትጻወቱሉ እትደልዩ መጠን ምጥራይኩም ከምኡ'ውን እትረኽብዎ ሽልማት ጸወታ ምረጹ።",
      step2Title: "ስጉምቲ 2: ካርቴላኹም ምረጹ",
      step2Desc1: "ናይ ምጥራይኩም ምስመረጽኩም ናብ ገጽ ምርጫ ካርቴላ ክትከዱ ኢኹም።",
      step2Desc2: "ኣብዚ ንዝተፈላለዩ ፍሉያት ቢንጎ ካርቴላታት (ቦርድታት) ናይቲ ጸወታ ክትዕዘቡ ትኽእሉ ኢኹም።",
      step2Desc3: "ነፍሲ ወከፍ ካርቴላ ፍሉይ ID ከምኡ'ውን ቁጽርታት ኣለዎ። ኣብሊዕሉዎ፣ ማእከላይ ቦታ ናይ 'N' ዓምዲ ኩሉ ግዜ ነጻ ቦታ እዩ!",
      step2Desc4: "ክትጻወቱሉ እትደልዩ ካርቴላ ምረጹ። እዚ ካርቴላ እዚ ንዓኹም ክሕዝ እዩ።",
      step3Title: "ስጉምቲ 3: ኣረጋግጽን ጽወት!",
      step3Desc1: "ካርቴላኹም ምስመረጽኩም ምርጫኹም ከተረጋግጹ ኢኹም፣ ከምኡ'ውን እቲ ዝተጠራየ ገንዘብ ካብ ሕሳብኩም ክቅነስ እዩ።",
      step3Desc2: "እቲ ጸወታ ድሕሪ ቁሩብ ግዜ ክጅምር እዩ። ኩዕሶታት ሓደ ብሓደ ክጽውዑ እዮም።",
      step3Desc3: "ቁጽሪታት ክጽውዑ ከለዉ፣ ኣብ ዲጂታል ካርቴላኹም ምልክት ግበሩ። 'F' (ነጻ) ቦታ ብኣውቶማቲክ ምልክት ይግበር።",
      step3Desc4: "ኣብ ቦርድኩም ክትከታተሉ ኣሎኩም!",
      step4Title: "ስጉምቲ 4: ቢንጎኹም ተብጽሑ!",
      step4Desc1: "ሓሙሽተ ቁጽሪታት ብትኽክል ብሓደ መስመር (ብምጥብባር፣ ብቁመትን፣ አርባዕተ ኩርናዓት ወይ ብሰያፍ) ምስመልከትኩም፣ ቢንጎ ኣለኩም!",
      step4Desc2: "ሽልማትኩም ንምብጻሕ ብቕጽበት \"ቢንጎ!\" ዝብል ቁልፊ ጽቀጡ።",
      step4Desc3: "ሲስተምና ቢንጎኹም ከረጋግጽ እዩ። ቅኑዕ እንተኾይኑ፣ ሽልማት ናይቲ ጸወታ ተዓዊትኩም፣ ከምኡ'ውን እቲ ገንዘብ ናብ ገንዘብ መክዘኒኹም ክውሰኽ እዩ!",
      step4Desc4: "ሓያሎ ተጻወትቲ ብሓደ ግዜ ቢንጎ እንተበጺሖም፣ ሽልማት ክምቀል ይኽእል፣ ወይ ድማ እቲ ቀዳማይ ቅኑዕ ጥሪት ይዕወት (ብመሰረት ሕጊ ጸወታ)።",
      goodLuck: "መልካም ዕድልን ብቢንጎ ምጽዋትን ኣስተማቅሩ!",
      birr: "ብር",
      fourCorners: "አርባዕተ ኩርናዓት"
    },
    wo: {
      howToPlay: "Akkaataa Bingo Itti Taphachuu Qabdan",
      welcomeMessage: "Gara Bingoatti baga nagaadhaan dhuftan! Tarkaanfiiwwan salphaa kana hordofuudhaan taphachuu fi injifannoo guddaa argachuu jalqabaa!",
      step1Title: "Tarkaanfii 1: Kaffaltii Keessan Filadhaa",
      step1Desc1: "Daashboordii ykn saayidbaarii irraa gara kutaa \"Tapha Bingo\" deemuu.",
      step1Desc2: "Taphawwan adda addaa kaffaltii adda addaa (fkn, 10 Birr, 20 Birr, kkf) fi injifannoo isaan irraa argachuu dandeessan ni argitu.",
      step1Desc3: "Tapha kaffaltii taphachuu barbaaddan irratti hundaa'uun filadhaa.",
      step2Title: "Tarkaanfii 2: Kaartaa Keessan Filadhaa",
      step2Desc1: "Kaffaltii filannoo booda gara fuula filannoo kaartaa deemaa.",
      step2Desc2: "Asitti, kaartaawwan Bingo adda addaa (boordii) tapha sanaaf argaman ilaaluu dandeessu.",
      step2Desc3: "Kaartaan tokkoon tokkoon ID addaa fi lakkoofsota mataa isaa qaba. Yaadadhaa, bakki gidduu tii fiixee 'N' yeroo hundaa bakka BILISA ti!",
      step2Desc4: "Kaartaa taphachuu barbaaddan filadhaa. Kaartaan kun isiniif qabamee jira.",
      step3Title: "Tarkaanfii 3: Mirkaneessaa fi Taphadhaa!",
      step3Desc1: "Yeroo kaartaa keessan filattan, filannoo keessan akka mirkaneessitan isin gaafatama, fi kaffaltiin sun baasii keessan irraa hir'ifama.",
      step3Desc2: "Taphichi yeroo gabaabaa keessatti ni jalqaba. Baalonni tokko tokkoon ni waamamuu.",
      step3Desc3: "Lakkoofsota yeroo waamaman, kaartaa keessan dijitaalaa irratti mallattoo godhaa. Bakki 'F' (Bilisa) ofiin ofii mallattoo godhama.",
      step3Desc4: "Boordii keessan irratti ija godhaa!",
      step4Title: "Tarkaanfii 4: Bingo keessan Gaafadhaa!",
      step4Desc1: "Yeroo lakkoofsota shan tooraan (horizontal, vertical, four corners ykn diagonal) milkaa'inaan mallattoo gootan, Bingo qabduu!",
      step4Desc2: "Yeroodhuma sana \"Bingo!\" jedhu tuquudhaan badhaasa keessan gaafadhaa.",
      step4Desc3: "Sirni keenya Bingo keessan ni mirkaneessa. Yoo sirrii ta'e, badhaasa taphichaa ni injifattu, fi maallaqni gara boorsa keessaniitti ni dabalamuu!",
      step4Desc4: "Yoo taphattoonni hedduun yeroo walfakkaataa Bingo gaafatan, badhaasichi ni qoodamuu danda'a, ykn gaaffiin jalqabaa fi sirrii ta'e ni injifata (seera taphaa irratti hundaa'uun).",
      goodLuck: "Carraa gaarii fi Bingo taphachuudhaan gammadaa!",
      birr: "Birr",
      fourCorners: "mucaawwan arfan"
    }
  };

  // Memoized translation helper function to prevent unnecessary re-renders
  const t = useCallback((key, variables = {}) => {
    let translation = translations[language]?.[key] || translations.en[key] || key;
    for (const [placeholder, value] of Object.entries(variables)) {
      translation = translation.replace(`{${placeholder}}`, value);
    }
    return translation;
  }, [language, translations]); // Dependency on 'language' and 'translations'

  // Toast utility function for displaying notifications
  const showToast = useCallback((message, type = 'error') => {
    toast[type](message, {
      position: "top-center",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
    });
  }, []);

  // Effect hook to set the current page title on component mount
  useEffect(() => {
    props.setCurrentPage(t('howToPlay')); // Set current page title using translation
  }, [props, t]); // Dependencies for useEffect

  return (
    <PageWrapper {...props} backgroundClass="bg-gradient-to-br from-blue-900 to-indigo-900 text-white min-h-screen">
      <ToastContainer /> {/* ToastContainer for displaying notifications */}
      <div className="relative max-w-4xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-bold text-center mb-8 flex items-center justify-center space-x-3">
          <HelpCircleIcon className="w-8 h-8 text-yellow-400" />
          <span>{t('howToPlay')}</span>
        </h1>

        <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-xl shadow-xl p-6 mb-8 border border-white border-opacity-20">
          <p className="text-lg text-gray-200 mb-6 text-center">
            {t('welcomeMessage')}
          </p>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4 flex items-center space-x-2">
              <CreditCardIcon className="w-7 h-7 text-green-400" />
              <span>{t('step1Title')}</span>
            </h2>
            <ul className="list-disc list-inside space-y-2 text-gray-300 text-base ml-4">
              <li>{t('step1Desc1')}</li>
              <li>{t('step1Desc2', { birr: t('birr') })}</li>
              <li>{t('step1Desc3')}</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4 flex items-center space-x-2">
              <GamepadIcon className="w-7 h-7 text-blue-400" />
              <span>{t('step2Title')}</span>
            </h2>
            <ul className="list-disc list-inside space-y-2 text-gray-300 text-base ml-4">
              <li>{t('step2Desc1')}</li>
              <li>{t('step2Desc2')}</li>
              <li>{t('step2Desc3')}</li>
              <li>{t('step2Desc4')}</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4 flex items-center space-x-2">
              <img src="https://placehold.co/30x30/AA33FF/FFFFFF?text=B" alt="Bingo Ball" className="mr-2 rounded-full" />
              <span>{t('step3Title')}</span>
            </h2>
            <ul className="list-disc list-inside space-y-2 text-gray-300 text-base ml-4">
              <li>{t('step3Desc1')}</li>
              <li>{t('step3Desc2')}</li>
              <li>{t('step3Desc3')}</li>
              <li>{t('step3Desc4')}</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4 flex items-center space-x-2">
              <img src="https://placehold.co/30x30/FFD700/000000?text=WIN" alt="Winning Icon" className="mr-2" />
              <span>{t('step4Title')}</span>
            </h2>
            <ul className="list-disc list-inside space-y-2 text-gray-300 text-base ml-4">
              <li>{t('step4Desc1')}</li>
              <li>{t('step4Desc2')}</li>
              <li>{t('step4Desc3')}</li>
              <li>{t('step4Desc4')}</li>
            </ul>
          </section>

          <p className="text-lg text-gray-200 text-center font-bold">
            {t('goodLuck')}
          </p>
        </div>
      </div>
    </PageWrapper>
  );
};

export default HowToPlayPage;
